import { useMemo, useRef, useState } from "react";

import { CircleInformation, CloseIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import { sanitizeNumberInput } from "@/helpers/numberHelpers";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { cn } from "@/lib/utils";
import { formatBreakValue, roundBreakValue } from "@/utils/dataset";

import { twMerge } from "tailwind-merge";

/** A sibling break the edited range is validated against for overlap. */
export interface SiblingBreak {
  /** 1-based position shown in the overlap error ("Break X"). */
  index: number;
  start: number | null;
  end: number | null;
}

interface LabelRangePanelProps {
  /** Committed optional label for this break. */
  label: string;
  /** Committed range start (null = open-ended "Less", first row only). */
  start: number | null;
  /** Committed range end (null = open-ended "More", last row only). */
  end: number | null;
  /** First row: start is the fixed "Less" label (only the end is editable). */
  isFirst?: boolean;
  /** Last row: end is the fixed "More" label (only the start is editable). */
  isLast?: boolean;
  /** Dataset numeric bounds for the out-of-range checks. On the Less/More edges the
   * message shows without blocking Save; any other bound blocks. */
  min?: number;
  max?: number;
  /**
   * The valid input window for THIS break (bounded by its adjacent breaks),
   * shown as the "Min … - Max …" hint. Falls back to the dataset min/max.
   */
  inputMin?: number;
  inputMax?: number;
  /** Integer attribute → entered values round to whole numbers (no decimals). */
  isInteger?: boolean;
  /** Other breaks — the edited range must not overlap any of them. */
  siblings: SiblingBreak[];
  /** Commit the edits (label + numeric start/end). */
  onSave: (label: string, start: number | null, end: number | null) => void;
  onClose: () => void;
}

const lo = (value: number | null) => (value === null ? -Infinity : value);
const hi = (value: number | null) => (value === null ? Infinity : value);

/** Half-open (start, end] ranges overlap when each starts before the other ends. */
const overlaps = (a: SiblingBreak, b: SiblingBreak) =>
  lo(a.start) < hi(b.end) && lo(b.start) < hi(a.end);

/** Parses a numeric field; returns `null` for empty or incomplete input. */
const parseField = (raw: string, isInteger: boolean): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? roundBreakValue(n, isInteger) : null;
};

const LabelRangePanel = ({
  label,
  start,
  end,
  isFirst = false,
  isLast = false,
  min = 0,
  max = 0,
  isInteger = false,
  siblings,
  onSave,
  onClose
}: LabelRangePanelProps) => {
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, onClose, []);

  const [draftLabel, setDraftLabel] = useState(label);
  // Numeric fields are edited as raw strings so intermediate states (empty, "-",
  // "1.") don't fight the parse; committed on Save.
  const [startText, setStartText] = useState(
    start === null ? "" : String(start)
  );
  const [endText, setEndText] = useState(end === null ? "" : String(end));

  const { error, blocksSave, nextStart, nextEnd } = useMemo(() => {
    // Fixed open-ended edges keep their `null`; the other side is user-editable.
    const parsedStart = isFirst ? null : parseField(startText, isInteger);
    const parsedEnd = isLast ? null : parseField(endText, isInteger);

    // Empty required field: block Save (PRD: revert to previous).
    if (!isFirst && parsedStart === null)
      return { error: "Start value is required.", blocksSave: true };
    if (!isLast && parsedEnd === null)
      return { error: "End value is required.", blocksSave: true };

    // Start must be strictly less than end.
    if (parsedStart !== null && parsedEnd !== null && parsedStart >= parsedEnd)
      return {
        error: "End value must be greater than start value.",
        blocksSave: true
      };

    // The Less/More edges only report being outside the data (ORB-3573): the break
    // may end up empty, and values outside it fall into "Others".
    const endBelowMin = isFirst && parsedEnd !== null && parsedEnd < min;
    const startAboveMax = isLast && parsedStart !== null && parsedStart > max;
    let outOfRange: string | undefined;
    if (endBelowMin)
      outOfRange = `This value is outside the current data range (MIN: ${formatBreakValue(
        min
      )}). This break may have no data assigned.`;
    else if (startAboveMax)
      outOfRange = `This value is outside the current data range (MAX: ${formatBreakValue(
        max
      )}). This break may have no data assigned.`;

    // Any other bound outside MIN/MAX still blocks Save, e.g. a middle break.
    const startOutside =
      !startAboveMax &&
      parsedStart !== null &&
      (parsedStart < min || parsedStart > max);
    const endOutside =
      !endBelowMin &&
      parsedEnd !== null &&
      (parsedEnd < min || parsedEnd > max);
    if (startOutside || endOutside)
      return {
        error: `Part of this range is outside the dataset (MIN: ${min} / MAX: ${max}).`,
        blocksSave: true
      };

    // In-range values must still not overlap any other break (block).
    const candidate: SiblingBreak = {
      index: 0,
      start: parsedStart,
      end: parsedEnd
    };
    const clash = siblings.find((sib) => overlaps(candidate, sib));
    if (clash)
      return {
        error: `Range overlaps with Break ${clash.index}. Please adjust the value.`,
        blocksSave: true
      };

    return {
      error: outOfRange,
      blocksSave: false,
      nextStart: parsedStart,
      nextEnd: parsedEnd
    };
  }, [startText, endText, isFirst, isLast, siblings, min, max, isInteger]);

  // Only a blocking error disables Save; the Less/More message does not.
  const canSave = !blocksSave;

  const handleSave = () => {
    if (!canSave) return;
    onSave(draftLabel.trim(), nextStart ?? null, nextEnd ?? null);
    onClose();
  };

  const rangeInputClass = (invalid: boolean) =>
    cn(
      // `flex-1 min-w-0` so the editable input and the fixed "Less"/"More" label
      // (also flex-1) each take exactly half of the row.
      "h-9 flex-1 min-w-0 rounded-lg border px-3 text-xs text-txtHeading outline-none",
      invalid
        ? "border-cinnabar focus:border-cinnabar"
        : "border-gainsboro focus:border-primary-teal"
    );

  return (
    <div
      ref={panelRef}
      data-testid="label-range-panel"
      className={twMerge(
        "z-[99] font-sans bg-white rounded-lg w-[338px]",
        "flex flex-col py-[16px] fixed left-[430px] top-[30px] text-xs"
      )}
    >
      {/* Header */}
      <div className="flex border-b border-gainsboro flex-row items-start justify-between px-4 pb-2">
        <h2 className="text-txtHeading text-[20px] font-bold">Value</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close label range"
          data-testid="label-range-close"
          className="border-0 bg-transparent p-0"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>

      <div className="mt-3 flex flex-col gap-3 px-4">
        {/* Optional label */}
        <div className="flex flex-col gap-1">
          <input
            type="text"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.currentTarget.value)}
            placeholder="No label (optional)"
            className="w-full rounded-lg border border-gainsboro px-3 py-2 text-xs text-txtHeading outline-none focus:border-primary-teal"
            data-testid="label-range-label"
          />
          <span className="text-[10px] text-grey">
            {MESSAGE.CUSTOM_RANGE_INFO_LABEL}
          </span>
        </div>

        {/* Range value */}
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-txtHeading text-sm font-semibold">
            Range Value
            <Tooltip
              content={MESSAGE.CUSTOM_RANGE_INFO_TOOLTIP}
              position="top-center"
            >
              <span
                className="inline-flex cursor-help text-grey"
                data-testid="label-range-info"
                aria-label={MESSAGE.CUSTOM_RANGE_INFO_TOOLTIP}
              >
                <CircleInformation className="size-3.5 fill-txtBody" />
              </span>
            </Tooltip>
          </span>
          <span className="text-[10px] text-grey">
            Min {formatBreakValue(min)} - Max {formatBreakValue(max)}
          </span>
          <div className="flex items-center gap-2">
            {isFirst ? (
              <span
                className="flex h-9 flex-1 items-center rounded-lg border border-gainsboro bg-whisper px-3 text-xs text-grey"
                data-testid="label-range-start-fixed"
              >
                Less
              </span>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={startText}
                onChange={(e) => {
                  const next = sanitizeNumberInput(
                    e.currentTarget.value,
                    isInteger
                  );
                  if (next !== null) setStartText(next);
                }}
                aria-label="Range start"
                data-testid="label-range-start"
                className={rangeInputClass(!!error)}
              />
            )}
            <span className="text-grey">–</span>
            {isLast ? (
              <span
                className="flex h-9 flex-1 items-center rounded-lg border border-gainsboro bg-whisper px-3 text-xs text-grey"
                data-testid="label-range-end-fixed"
              >
                More
              </span>
            ) : (
              <input
                type="text"
                inputMode="decimal"
                value={endText}
                onChange={(e) => {
                  const next = sanitizeNumberInput(
                    e.currentTarget.value,
                    isInteger
                  );
                  if (next !== null) setEndText(next);
                }}
                aria-label="Range end"
                data-testid="label-range-end"
                className={rangeInputClass(!!error)}
              />
            )}
          </div>
          {error && (
            <span
              className="text-[10px] text-cinnabar"
              data-testid="label-range-error"
            >
              {error}
            </span>
          )}
        </div>

        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          className="h-8 rounded-lg text-xs"
          data-testid="label-range-save"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default LabelRangePanel;
